import React, { useState, useEffect } from 'react'
import {
  Typography,
  Box,
  Button,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  Add,
  Search,
  ExpandMore,
  CalendarToday,
  Home,
  Visibility,
  Edit,
} from '@mui/icons-material'
import { useQueryClient } from '@tanstack/react-query'
import { SidebarLayout } from '@/components/Layout'
import { BookingCard, BookingForm, BookingDetails } from '@/components/Bookings'
import { Booking, Listing } from '@/types'
import { BookingsService, BookingForm as BookingFormData } from '@/utils/bookings-service'
import { ListingsService } from '@/utils/listings-service'
import { BookingWithGuestStatus, guestKeys } from '@/hooks/api/useGuests'
import { bookingKeys } from '@/hooks/api/useBookings'
import { calendarBookingsKeys } from '@/hooks/api/useCalendarBookings'
import { useAuth } from '@/contexts/AuthContext'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`bookings-tabpanel-${index}`}
      aria-labelledby={`bookings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  )
}

interface BookingsTableProps {
  bookings: (Booking | BookingWithGuestStatus)[]
  listings: Listing[]
  onView: (booking: Booking | BookingWithGuestStatus) => void
  onEdit: (booking: Booking | BookingWithGuestStatus) => void
}

function BookingsTable({ bookings, listings, onView, onEdit }: BookingsTableProps) {
  return (
    <TableContainer sx={{ overflow: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Booking ID</TableCell>
            <TableCell>Property</TableCell>
            <TableCell>Dates</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Amount</TableCell>
            <TableCell align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {bookings
            .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
            .map((booking) => {
              const listing = listings.find(l => l.listingId === booking.listingId)
              const statusColor = BookingsService.getStatusColor(booking.status)
              const dateRange = BookingsService.formatDateRange(booking.startDate, booking.endDate)
              return (
                <TableRow key={booking.bookingId} hover sx={{ cursor: 'pointer' }} onClick={() => onView(booking)}>
                  <TableCell sx={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {booking.bookingId.slice(-8)}
                  </TableCell>
                  <TableCell>{listing?.title ?? 'Unknown Property'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateRange}</TableCell>
                  <TableCell>
                    <Chip
                      label={booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
                      color={statusColor}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">${booking.totalPrice.toFixed(2)}</TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onView(booking) }} aria-label="View booking">
                      <Visibility fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(booking) }} aria-label="Edit booking">
                      <Edit fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              )
            })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default function MyBookings() {
  const { userProfile } = useAuth()
  const queryClient = useQueryClient()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [bookings, setBookings] = useState<Booking[]>([])
  const [listings, setListings] = useState<Listing[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Tab and filter state
  const [activeTab, setActiveTab] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [listingFilter, setListingFilter] = useState<string>('all')
  
  // Dialog state
  const [formOpen, setFormOpen] = useState(false)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [editingBooking, setEditingBooking] = useState<Booking | BookingWithGuestStatus | null>(null)
  const [selectedBooking, setSelectedBooking] = useState<Booking | BookingWithGuestStatus | null>(null)



  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    setError(null)
    
    try {
      // Load both bookings and listings for hosts
      const [bookingsData, listingsData] = await Promise.all([
        BookingsService.getBookings(),
        ListingsService.getListings(),
      ])
      
      setBookings(bookingsData)
      setListings(listingsData)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bookings')
    } finally {
      setLoading(false)
    }
  }

  // Use regular bookings
  const displayBookings = bookings
  const displayLoading = loading
  const displayError = error

  const handleCreateBooking = async (bookingData: BookingFormData) => {
    const newBooking = await BookingsService.createBooking(bookingData)
    setBookings(prev => [newBooking, ...prev])
    
    // Invalidate all booking-related queries to ensure fresh data everywhere
    queryClient.invalidateQueries({ queryKey: guestKeys.bookingsWithStatus() })
    queryClient.invalidateQueries({ queryKey: bookingKeys.lists() })
    queryClient.invalidateQueries({ queryKey: calendarBookingsKeys.all })
  }

  const handleUpdateBooking = async (bookingData: BookingFormData) => {
    if (!editingBooking) return
    
    const updatedBooking = await BookingsService.updateBooking(editingBooking.bookingId, bookingData)
    setBookings(prev => prev.map(b => b.bookingId === updatedBooking.bookingId ? updatedBooking : b))
    setEditingBooking(null)
    
    // Invalidate all booking-related queries to ensure fresh data everywhere
    queryClient.invalidateQueries({ queryKey: guestKeys.bookingsWithStatus() })
    queryClient.invalidateQueries({ queryKey: guestKeys.detail(editingBooking.bookingId) })
    queryClient.invalidateQueries({ queryKey: bookingKeys.lists() })
    queryClient.invalidateQueries({ queryKey: bookingKeys.detail(editingBooking.bookingId) })
    queryClient.invalidateQueries({ queryKey: calendarBookingsKeys.all })
  }

  const handleStatusChange = async (booking: Booking | BookingWithGuestStatus, status: string) => {
    try {
      let updatedBooking: Booking
      
      if (status === 'payment-confirmed') {
        // Handle payment confirmation separately
        updatedBooking = await BookingsService.confirmPayment(booking.bookingId, booking.listingId)
      } else {
        // Handle regular status updates
        updatedBooking = await BookingsService.updateBookingStatus(booking.bookingId, status as any, booking.listingId)
      }
      
      // Update the regular bookings state
      setBookings(prev => prev.map(b => b.bookingId === updatedBooking.bookingId ? updatedBooking : b))
      
      // Invalidate all booking-related queries to ensure fresh data everywhere
      queryClient.invalidateQueries({ queryKey: guestKeys.bookingsWithStatus() })
      queryClient.invalidateQueries({ queryKey: guestKeys.detail(booking.bookingId) })
      queryClient.invalidateQueries({ queryKey: bookingKeys.lists() })
      queryClient.invalidateQueries({ queryKey: bookingKeys.detail(booking.bookingId) })
      queryClient.invalidateQueries({ queryKey: calendarBookingsKeys.all })
      // Also invalidate booking confirmation status
      queryClient.invalidateQueries({ queryKey: ['booking-confirmation-status', booking.bookingId] })
      queryClient.invalidateQueries({ queryKey: ['booking-communications', booking.bookingId] })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update booking status')
    }
  }

  const handleCancelBooking = async (booking: Booking | BookingWithGuestStatus) => {
    if (!confirm('Are you sure you want to cancel this booking? A cancellation notification will be sent to the guest.')) {
      return
    }
    
    try {
      const result = await BookingsService.cancelBooking(booking.bookingId, booking.listingId)
      
      // Update the regular bookings state
      setBookings(prev => prev.map(b => b.bookingId === result.booking.bookingId ? result.booking : b))
      
      // Invalidate all booking-related queries to ensure fresh data everywhere
      queryClient.invalidateQueries({ queryKey: guestKeys.bookingsWithStatus() })
      queryClient.invalidateQueries({ queryKey: guestKeys.detail(booking.bookingId) })
      queryClient.invalidateQueries({ queryKey: bookingKeys.lists() })
      queryClient.invalidateQueries({ queryKey: bookingKeys.detail(booking.bookingId) })
      queryClient.invalidateQueries({ queryKey: calendarBookingsKeys.all })
      // Also invalidate booking confirmation status
      queryClient.invalidateQueries({ queryKey: ['booking-confirmation-status', booking.bookingId] })
      queryClient.invalidateQueries({ queryKey: ['booking-communications', booking.bookingId] })
      
      setError(null)
      console.log(result.message)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel booking')
    }
  }

  const openCreateForm = () => {
    setEditingBooking(null)
    setFormOpen(true)
  }

  const openEditForm = (booking: Booking | BookingWithGuestStatus) => {
    setEditingBooking(booking)
    setFormOpen(true)
  }

  const openDetails = (booking: Booking | BookingWithGuestStatus) => {
    setSelectedBooking(booking)
    setDetailsOpen(true)
  }

  // Filter bookings based on current filters
  const filteredBookings = displayBookings.filter(booking => {
    const matchesSearch = searchTerm === '' || 
      booking.bookingId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.guestId.toLowerCase().includes(searchTerm.toLowerCase())
    
    const matchesStatus = statusFilter === 'all' || booking.status === statusFilter
    const matchesListing = listingFilter === 'all' || booking.listingId === listingFilter
    
    return matchesSearch && matchesStatus && matchesListing
  })

  // Group bookings by status for tabs
  const bookingsByStatus = {
    all: filteredBookings,
    pending: filteredBookings.filter(b => b.status === 'pending'),
    confirmed: filteredBookings.filter(b => b.status === 'confirmed'),
    completed: filteredBookings.filter(b => b.status === 'completed'),
    cancelled: filteredBookings.filter(b => b.status === 'cancelled'),
  }

  // Group bookings by property
  const bookingsByProperty = filteredBookings.reduce((acc, booking) => {
    const listing = listings.find(l => l.listingId === booking.listingId)
    const propertyName = listing?.title || 'Unknown Property'
    
    if (!acc[propertyName]) {
      acc[propertyName] = []
    }
    acc[propertyName].push(booking)
    return acc
  }, {} as Record<string, (Booking | BookingWithGuestStatus)[]>)

  const getSelectedBookingListing = () => {
    if (!selectedBooking) return undefined
    return listings.find(l => l.listingId === selectedBooking.listingId)
  }

  if (displayLoading) {
    return (
      <SidebarLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </SidebarLayout>
    )
  }

  return (
    <SidebarLayout>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" gutterBottom>
            My Bookings
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage bookings for your properties
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={openCreateForm}
        >
          Add Booking
        </Button>
      </Box>

      {displayError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {typeof displayError === 'string' ? displayError : 
           (displayError as any) instanceof Error ? (displayError as Error).message : 
           String(displayError)}
        </Alert>
      )}

      {/* Filters and View Toggle */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="Search bookings..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
        />
        
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="confirmed">Confirmed</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Property</InputLabel>
          <Select
            value={listingFilter}
            label="Property"
            onChange={(e) => setListingFilter(e.target.value)}
          >
            <MenuItem value="all">All Properties</MenuItem>
            {listings.map((listing) => (
              <MenuItem key={listing.listingId} value={listing.listingId}>
                {listing.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>


      </Box>

      {/* Status Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab label={`All (${bookingsByStatus.all.length})`} />
          <Tab label={`Pending (${bookingsByStatus.pending.length})`} />
          <Tab label={`Confirmed (${bookingsByStatus.confirmed.length})`} />
          <Tab label={`Completed (${bookingsByStatus.completed.length})`} />
          <Tab label={`Cancelled (${bookingsByStatus.cancelled.length})`} />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        {Object.keys(bookingsByProperty).length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No bookings found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {bookings.length === 0 
                ? 'Create your first booking to get started'
                : 'Try adjusting your filters to see more results'
              }
            </Typography>
            <Button variant="contained" onClick={openCreateForm}>
              Create Booking
            </Button>
          </Box>
        ) : isMobile ? (
          Object.entries(bookingsByProperty).map(([propertyName, propertyBookings]) => (
            <Accordion key={propertyName} defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Home color="action" />
                  <Typography variant="h6">{propertyName}</Typography>
                  <Chip label={`${propertyBookings.length} booking${propertyBookings.length !== 1 ? 's' : ''}`} size="small" />
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {propertyBookings
                  .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
                  .map((booking) => (
                    <BookingCard
                      key={booking.bookingId}
                      booking={booking}
                      listingTitle={propertyName}
                      onEdit={openEditForm}
                      onCancel={handleCancelBooking}
                      onStatusChange={handleStatusChange}
                      onClick={openDetails}
                    />
                  ))}
              </AccordionDetails>
            </Accordion>
          ))
        ) : (
          <BookingsTable
            bookings={filteredBookings}
            listings={listings}
            onView={openDetails}
            onEdit={openEditForm}
          />
        )}
      </TabPanel>

      {/* Other tab panels with filtered content */}
      {[1, 2, 3, 4].map((tabIndex) => {
        const statusKey = ['pending', 'confirmed', 'completed', 'cancelled'][tabIndex - 1]
        const statusBookings = bookingsByStatus[statusKey as keyof typeof bookingsByStatus]
        
        return (
          <TabPanel key={tabIndex} value={activeTab} index={tabIndex}>
            {statusBookings.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No {statusKey} bookings
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Bookings with {statusKey} status will appear here
                </Typography>
              </Box>
            ) : isMobile ? (
              statusBookings
                .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
                .map((booking) => {
                  const listing = listings.find(l => l.listingId === booking.listingId)
                  return (
                    <BookingCard
                      key={booking.bookingId}
                      booking={booking}
                      listingTitle={listing?.title}
                      onEdit={openEditForm}
                      onCancel={handleCancelBooking}
                      onStatusChange={handleStatusChange}
                      onClick={openDetails}
                    />
                  )
                })
            ) : (
              <BookingsTable
                bookings={statusBookings}
                listings={listings}
                onView={openDetails}
                onEdit={openEditForm}
              />
            )}
          </TabPanel>
        )
      })}

      {/* Booking Form Dialog */}
      <BookingForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={editingBooking ? handleUpdateBooking : handleCreateBooking}
        booking={editingBooking as Booking}
        listings={listings}
      />

      {/* Booking Details Dialog */}
      <BookingDetails
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        booking={selectedBooking as Booking}
        listing={getSelectedBookingListing()}
        onEdit={openEditForm}
        onCancel={handleCancelBooking}
      />
    </SidebarLayout>
  )
}